/**
 * When the user switches to Advanced mode, we dispatch "highlight-advanced-mode-features"
 * and open a short pending window. The switch happens on the settings route, which unmounts
 * the prompt bar, the scope pill and the main nav, so the live event only reaches whatever
 * happens to be on screen; everything else claims the pending window when it mounts.
 */
import { dispatchGlobalEvent } from "@storytell/events";

/** How long an affordance has to mount and still pick up the highlight. */
const PENDING_MS = 4000;

/** The affordances that glow when the user switches to Advanced. Each claims the window once. */
type HighlightTarget = "nav-icons" | "prompt-bar" | "scope-pill";

let pendingUntil = 0;
const claimed = new Set<HighlightTarget>();

/** Call when the user switches to Advanced. */
export function announceAdvancedModeSwitch(): void {
  pendingUntil = Date.now() + PENDING_MS;
  claimed.clear();
  dispatchGlobalEvent("highlight-advanced-mode-features", undefined);
}

/**
 * True once per switch per target, so an affordance that mounts after the switch still
 * highlights, and a remount inside the same window doesn't highlight it a second time.
 */
export function consumePendingHighlight(target: HighlightTarget): boolean {
  if (Date.now() > pendingUntil) {
    pendingUntil = 0;
    claimed.clear();
    return false;
  }
  if (claimed.has(target)) return false;
  claimed.add(target);
  return true;
}
